import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, Upload, Database, Sparkle } from '@phosphor-icons/react';
import CellRevealLogo from '../ui/CellRevealLogo';

export function CheckoutSuccess() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-24 pb-12 bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Card */}
        <Card className="p-12 text-center mb-8">
          <div className="flex justify-center mb-6">
            <CellRevealLogo variant="light" size="2xl" animated />
          </div>

          <h1 className="text-foreground mb-4">Welcome to Professional.</h1>
          <p className="text-muted-foreground mb-2 max-w-xl mx-auto">
            Your 14-day free trial has started. You now have full access to all Professional features.
          </p>
          <p className="text-muted-foreground/70">
            Confirmation email sent to your inbox
          </p>
        </Card>

        {/* What's Next */}
        <Card className="p-8 mb-8">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-6">
            What's next
          </p>

          <div className="grid md:grid-cols-2 gap-px bg-border border border-border rounded-xl overflow-hidden">
            <div className="p-6 bg-background flex flex-col">
              <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center mb-4">
                <Upload className="w-4 h-4 text-teal-600" />
              </div>
              <h4 className="mb-2 text-foreground">Upload your data</h4>
              <p className="text-muted-foreground text-sm mb-6 flex-1">
                Analyze your private datasets with full confidentiality.
              </p>
              <Button
                variant="default"
                className="w-full"
                onClick={() => navigate('/upload/private')}
              >
                Upload private data
              </Button>
            </div>

            <div className="p-6 bg-background flex flex-col">
              <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center mb-4">
                <Database className="w-4 h-4 text-teal-600" />
              </div>
              <h4 className="mb-2 text-foreground">Browse public data</h4>
              <p className="text-muted-foreground text-sm mb-6 flex-1">
                Explore 1,000+ curated public datasets, now unlimited.
              </p>
              <Button
                variant="outline"
                className="w-full border-border hover:bg-muted/30"
                onClick={() => navigate('/public-data')}
              >
                Browse datasets
              </Button>
            </div>
          </div>
        </Card>

        {/* Quick Start Guide */}
        <Card className="p-8">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-6">
            Quick start
          </p>

          <div className="divide-y divide-border">
            {[
              { n: '01', title: 'Upload or select data', body: 'Upload your own files or choose from curated public datasets.' },
              { n: '02', title: 'Configure analysis', body: 'Select tissue-specific signatures and pick deconvolution algorithms.' },
              { n: '03', title: 'Review results', body: 'Explore interactive visualizations and export data for publication.' },
              { n: '04', title: 'Use the API', body: 'Automate your workflow — find your API key in account settings.' },
            ].map(({ n, title, body }) => (
              <div key={n} className="py-5 flex items-start gap-6">
                <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground/30 mt-1 flex-shrink-0 w-6">{n}</span>
                <div>
                  <h4 className="mb-1 text-foreground">{title}</h4>
                  <p className="text-muted-foreground text-sm">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Professional Features Highlight */}
        <Card className="p-8 mt-8 bg-gradient-to-br from-navy-900 to-navy-950 text-white">
          <div className="flex items-center gap-3 mb-6">
            <Sparkle className="w-8 h-8" />
            <h3 className="text-white">You Now Have Access To</h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>Unlimited private data uploads</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>Priority processing queue</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>Advanced visualizations</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>API access</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>90-day data retention</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>Email support</span>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <Button 
            variant="default"
            onClick={() => navigate('/upload')}
          >
            Start your first analysis
          </Button>
          <Button 
            variant="secondary"
            onClick={() => navigate('/account')}
          >
            View account settings
          </Button>
        </div>
      </div>
    </div>
  );
}
